import * as React from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { useIntl } from 'react-intl';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { format, parseISO, isBefore, isAfter, startOfDay, isValid } from 'date-fns';
import { Calendar } from 'components/v2/calendar/calendar';
import { Modal } from 'components/shared/modal';
import { SwipeModalRef } from 'components/shared/modal/mobile-modal';
import { getDateFnsLocale } from 'shared/hooks/use-locale/date-fns-locale.utils';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';
import { DatePickerButtonProps } from './date-picker-button.types';

export const DatePickerButton = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  error,
  disabled = false,
  label,
  className,
  id,
  popoverAlign = 'bottom',
}: DatePickerButtonProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);
  const isSmallViewport = useIsSmallViewportWidth();
  const [open, setOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const modalRef = React.useRef<SwipeModalRef>(null);

  // Close popover when clicking outside (only for desktop popover)
  React.useEffect(() => {
    if (isSmallViewport || !open) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, isSmallViewport]);

  const handleToggleOpen = React.useCallback((): void => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleClose = React.useCallback((): void => {
    setOpen(false);
  }, []);

  // Handle calendar selection
  const handleCalendarSelect = React.useCallback(
    (selected: string | { from: string; to: string | null } | string[] | null): void => {
      if (typeof selected === 'string') {
        try {
          const selectedDate = parseISO(selected);
          if (isValid(selectedDate)) {
            // Validate against minDate and maxDate
            const dateStart = startOfDay(selectedDate);

            if (minDate) {
              const minDateStart = startOfDay(minDate);
              if (isBefore(dateStart, minDateStart)) {
                return; // Don't allow selection before minDate
              }
            }

            if (maxDate) {
              const maxDateStart = startOfDay(maxDate);
              if (isAfter(dateStart, maxDateStart)) {
                return; // Don't allow selection after maxDate
              }
            }

            onChange(selectedDate);

            // Close modal/popover after selection
            if (isSmallViewport && modalRef.current) {
              modalRef.current.closeModal(() => setOpen(false));
            } else {
              setOpen(false);
            }
          }
        } catch {
          // Ignore invalid dates
        }
      } else if (selected === null) {
        onChange(null);
      }
    },
    [onChange, isSmallViewport, minDate, maxDate],
  );

  // Get calendar selected value in single date format (YYYY-MM-DD string)
  const getCalendarSelected = React.useCallback((): string | null => {
    if (!value || !isValid(value)) {
      return null;
    }
    return format(value, 'yyyy-MM-dd');
  }, [value]);

  // Determine if we should disable past dates (when minDate is today or in the future)
  const shouldDisablePast = React.useMemo(() => {
    if (!minDate) {
      return false;
    }
    const today = startOfDay(new Date());
    const minDateStart = startOfDay(minDate);
    return !isBefore(minDateStart, today);
  }, [minDate]);

  // Display the date in the button
  const getDisplayValue = (): string => {
    if (!value || !isValid(value)) {
      return (
        placeholder ||
        formatMessage({
          id: 'datePickerButton.selectDate',
          defaultMessage: 'Select date',
        })
      );
    }

    try {
      return format(value, 'P', { locale: dateFnsLocale });
    } catch {
      return (
        placeholder ||
        formatMessage({
          id: 'datePickerButton.selectDate',
          defaultMessage: 'Select date',
        })
      );
    }
  };

  const displayValue = getDisplayValue();
  const hasValue = value && isValid(value);

  // Calendar content (reusable for both desktop and mobile)
  const calendarContent = (
    <div className="p-4" data-cy="date-picker-calendar">
      <Calendar
        mode="single"
        selected={getCalendarSelected()}
        onSelect={handleCalendarSelect}
        className="rounded-md border-0"
        disablePast={shouldDisablePast}
        disableAfter={maxDate}
        locale={dateFnsLocale}
        todayLabel={formatMessage({
          id: 'createVisitFormV2.whenStep.calendarToday',
          defaultMessage: 'Today',
        })}
        selectedLabel={formatMessage({
          id: 'createVisitFormV2.whenStep.calendarSelected',
          defaultMessage: 'Selected',
        })}
      />
    </div>
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="relative" ref={popoverRef}>
        <Button
          type="button"
          variant="outline"
          onClick={handleToggleOpen}
          disabled={disabled}
          id={id}
          className={cn(
            'w-full justify-start text-left font-normal h-9 px-3 text-base',
            !hasValue && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          data-cy="date-picker-button"
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate text-sm">{displayValue}</span>
        </Button>

        {/* Desktop: Popover */}
        {open && !isSmallViewport && (
          <div
            className={cn(
              'absolute left-0 z-50 bg-white rounded-lg border border-gray-200 shadow-lg',
              popoverAlign === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
            )}
            data-cy="date-picker-popover"
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-2 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700 px-2">
                {label ||
                  formatMessage({
                    id: 'datePickerButton.selectDate',
                    defaultMessage: 'Select date',
                  })}
              </span>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                aria-label={formatMessage({
                  id: 'datePickerButton.close',
                  defaultMessage: 'Close',
                })}
                data-cy="date-picker-close"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            {calendarContent}
          </div>
        )}
      </div>

      {/* Mobile: Modal */}
      {open && isSmallViewport && (
        <Modal ref={modalRef} onClose={handleClose} variant="normal" withExitButton>
          <div className="flex flex-col h-full max-h-[80vh]" data-cy="date-picker-modal">
            <h2 className="text-lg font-semibold mb-4 px-4 pt-4">
              {label ||
                formatMessage({
                  id: 'datePickerButton.selectDate',
                  defaultMessage: 'Select date',
                })}
            </h2>
            {calendarContent}
          </div>
        </Modal>
      )}

      {/* Error message */}

      <div className="mt-1">{error && <p className="text-sm text-destructive">{error}</p>}</div>
    </div>
  );
};
